import React, { useEffect, useState } from "react";
import JobPostCaterAPI from "@frontend/app/api/JobPostCaterAPI.ts";
import NewCaterModal from "@frontend/app/catering/editing/NewCaterModal.tsx";
import _ from "lodash";
import ErrorBoundary from "@frontend/app/layout/ErrorBoundary.tsx";
import { DocComment } from "@mjjs/ui";
import JobCaterEditor from "@frontend/app/catering/editing/JobCaterEditor.tsx";

const JobCaterEditorParent = () => {
  const [loadingContent, setLoadingContent] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [requirementsComparison, setRequirementsComparison] = useState<any>({});
  const [newCaterModalOpen, setNewCaterModalOpen] = useState(false);
  const [showEditor, setShowEditor] = useState(true);
  const [cater, setCater] = useState<any>(null);
  const [caters, setCaters] = useState<any[]>([]);
  const [company, setCompany] = useState("");
  const [position, setPosition] = useState("");
  const [comments, setComments] = useState<Record<string, DocComment>>({});

  const listCaters = async () => {
    const fetchedCaters = await JobPostCaterAPI.list();
    setCaters(fetchedCaters);
    return fetchedCaters;
  };

  useEffect(() => {
    const fetchData = async () => {
      const fetchedCaters = await listCaters();
      if (fetchedCaters && fetchedCaters.length > 0) {
        const first = fetchedCaters[0];
        setCater(first);
        setRequirementsComparison(first.requirementsComparison);
        setCompany(first.company);
        setPosition(first.position);
        setComments(first.comments);
      }
    };

    fetchData()
      .catch(err => setError(err))
      .finally(() => setLoadingContent(false));
  }, []);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const selectedCater = caters.find((c: any) => c._id === selectedId);
    if (selectedCater) {
      setCater(selectedCater);
      setRequirementsComparison(selectedCater.requirementsComparison);
      setCompany(selectedCater.company);
      setPosition(selectedCater.position);
      setComments(selectedCater.comments);
      setShowEditor(false);
      setTimeout(() => {
        setShowEditor(true);
      }, 0);
    }
  };

  const handleEditorUpdate = ({ editor }: any) => {
    setRequirementsComparison(editor.getJSON());
  };

  const handleSave = async () => {
    await JobPostCaterAPI.update(cater._id, {
      company,
      position,
      requirementsComparison,
    });
    alert("Saved!");
  };

  // const onCommentsUpdated = (updatedComments: Record<string, DocComment>) => {
  //   // setComments(updatedComments);
  // };

  const onCommentAdded = async (comment: DocComment, commentID: string) =>
    JobPostCaterAPI.addComment(
      cater._id,
      { ..._.omit(comment, "imageFile"), id: commentID },
      comment.imageFile
    );

  const onCommentEdited = async (e: React.FormEvent, commentID: string, comment: DocComment) =>
    JobPostCaterAPI.updateComment(cater._id, commentID, { ..._.omit(comment, "imageFile") });

  const handleCreateCater = async (caterData: any) => {
    try {
      setNewCaterModalOpen(false);
      
      setCater(caterData);
      setCompany(caterData.company || "");
      setPosition(caterData.position || "");
      setComments(caterData.comments || {});
      setRequirementsComparison(caterData.requirementsComparison || {});
      
      // Refresh the caters list in the background
      try {
        await listCaters();
      } catch (err) {
        console.error("Failed to refresh caters list:", err);
        // Don't fail the whole operation if list fails
      }

      // Unmount the editor completely and re-mount to avoid errors
      setShowEditor(false);
      setTimeout(() => {
        setShowEditor(true);
      }, 0);
    } catch (err) {
      console.error("Error creating cater:", err);
      setError(err instanceof Error ? err : new Error("Failed to create cater"));
    }
  };

  if (loadingContent) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  const handleDelete = async () => {
    if (!cater) {
      return;
    }

    await JobPostCaterAPI.delete(cater._id);
    listCaters();
  };
  const handleCommentDelete = async (_: React.MouseEvent, commentID: string) => {
    if (!cater) {
      return;
    }

    const res = await JobPostCaterAPI.deleteComment(cater._id, commentID);
    setComments(res.comments);
    console.log("dingus comment delete", res);
  };

  const handleCopySharableLink = async () => {
    // Should end up with http or https + hostname (without routes) + /cater/caterID

    // Pull the parts used to construct the link
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;

    // Construct the link, don't use port if it's 80 or 443 (or nullish because it's prod)
    const link = `${protocol}//${hostname}${port !== "80" && port !== "443" && !!port ? `:${port}` : ""}/cater/${cater._id}`;

    // Copy to clipboard
    await navigator.clipboard.writeText(link);
  };

  return (
    <>
      <NewCaterModal
        isOpen={newCaterModalOpen}
        onClose={() => setNewCaterModalOpen(false)}
        onSuccess={handleCreateCater}
      />
      <div className="p-5 my-16">
        {/* Controls and input fields */}
        <div className="mb-4">
          <button onClick={() => setNewCaterModalOpen(true)}>New Cater</button>
          <button className="ml-4" onClick={handleDelete}>
            Delete Cater
          </button>
          <button onClick={handleCopySharableLink}>Copy Sharable Link</button>
        </div>
        <div className="mb-4">
          <label>Company:</label>
          <input type="text" value={company} onChange={e => setCompany(e.target.value)} />
        </div>
        <div className="mb-4">
          <label>Position:</label>
          <input type="text" value={position} onChange={e => setPosition(e.target.value)} />
        </div>
        <div className="mb-4">
          <label>Select Cater:</label>
          <select value={cater ? cater._id : ""} onChange={handleSelectChange}>
            {caters.map((c: any) => (
              <option key={c._id} value={c._id}>
                {c.company} - {c.position}
              </option>
            ))}
          </select>
        </div>

        <ErrorBoundary>
          {showEditor && !!cater && (
            <JobCaterEditor
              content={requirementsComparison}
              onUpdate={handleEditorUpdate}
              comments={comments}
              // onCommentsUpdated={onCommentsUpdated}
              onCommentAdded={onCommentAdded}
              onCommentDeleted={handleCommentDelete}
              onSave={handleSave}
              docID={cater._id}
              onCommentEdit={onCommentEdited}
            />
          )}
        </ErrorBoundary>
      </div>
    </>
  );
};

export default JobCaterEditorParent;
